import { useRef } from 'react';
import { InterviewFeedback, RevisedFeedback, ReviewSession } from '@recruitai/shared';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScoreDimensionBar } from './ScoreDimensionBar';
import { ReviewCompare } from './ReviewCompare';
import { ExportPdfButton } from '@/components/pdf/ExportPdfButton';
import { generateFileName } from '@/utils/pdf-utils';

interface ScoreReportProps {
  feedback: InterviewFeedback;
  finalScore: number;
  fitScore: number;
  candidateName?: string;
  candidateEmail?: string;
  interviewDate?: string;
  revisedFeedback?: RevisedFeedback;
  reviewSessions?: ReviewSession[];
  reviewCount?: number;
}

export function ScoreReport({
  feedback,
  finalScore,
  fitScore,
  candidateName,
  candidateEmail,
  interviewDate,
  revisedFeedback,
  reviewSessions,
  reviewCount = 0,
}: ScoreReportProps) {
  const hasRevision = !!revisedFeedback;
  const contentRef = useRef<HTMLDivElement>(null);

  if (!feedback || !feedback.feedback) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        Carregando resultados...
      </Card>
    );
  }
  return (
    <div className="space-y-6" ref={contentRef}>
      {/* Export PDF button — only rendered when candidate data is available */}
      {candidateName && (
        <div className="flex justify-end">
          <ExportPdfButton
            contentRef={contentRef as React.RefObject<HTMLElement>}
            fileName={generateFileName('interview', candidateName)}
            header={{
              candidateName,
              candidateEmail: candidateEmail ?? '',
              date: interviewDate
                ? new Date(interviewDate).toLocaleDateString('pt-BR')
                : '',
            }}
            disabled={!feedback}
            tooltipText="Resultado da entrevista não disponível"
          />
        </div>
      )}
      {/* Score principal */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Score Entrevista</p>
          <p className="text-3xl font-bold">
            {hasRevision ? revisedFeedback.overallScore : feedback.overallScore}
          </p>
          {hasRevision && (
            <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">Revisado</Badge>
          )}
        </Card>
        <Card className="p-4 ring-2 ring-primary">
          <p className="text-xs text-muted-foreground">Score Final</p>
          <p className="text-3xl font-bold text-primary">{finalScore}</p>
          <p className="text-xs text-muted-foreground mt-1">60% fit + 40% entrevista</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Fit Score (Currículo)</p>
          <p className="text-3xl font-bold">{fitScore}</p>
        </Card>
      </div>

      {/* Scores por dimensão */}
      <Card className="p-4 space-y-3">
        <h4 className="font-semibold text-sm">Scores por Dimensão</h4>
        <ScoreDimensionBar label="Técnico" score={feedback.technicalScore} />
        <ScoreDimensionBar label="Comportamental" score={feedback.behavioralScore} />
        <ScoreDimensionBar label="Comunicação" score={feedback.communicationScore} />
      </Card>

      {/* Feedback qualitativo */}
      <Card className="p-4 space-y-3">
        <h4 className="font-semibold text-sm">Feedback por Dimensão</h4>
        <div className="space-y-2 text-sm">
          <div>
            <p className="font-medium text-blue-700">Técnico</p>
            <p className="text-muted-foreground">{feedback.feedback.technical}</p>
          </div>
          <div>
            <p className="font-medium text-green-700">Comportamental</p>
            <p className="text-muted-foreground">{feedback.feedback.behavioral}</p>
          </div>
          <div>
            <p className="font-medium text-purple-700">Comunicação</p>
            <p className="text-muted-foreground">{feedback.feedback.communication}</p>
          </div>
        </div>
      </Card>

      {/* Highlights e Concerns */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 space-y-2">
          <h4 className="font-semibold text-sm text-green-700">✅ Pontos Positivos</h4>
          <ul className="text-sm space-y-1">
            {feedback.highlights.map((h, i) => (
              <li key={i} className="text-muted-foreground">• {h}</li>
            ))}
          </ul>
        </Card>
        <Card className="p-4 space-y-2">
          <h4 className="font-semibold text-sm text-amber-700">⚠️ Pontos de Atenção</h4>
          <ul className="text-sm space-y-1">
            {feedback.concerns.map((c, i) => (
              <li key={i} className="text-muted-foreground">• {c}</li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Summary */}
      <Card className="p-4">
        <h4 className="font-semibold text-sm mb-2">Resumo Executivo</h4>
        <p className="text-sm text-muted-foreground">{feedback.summary}</p>
      </Card>

      {/* Review comparison */}
      {hasRevision && (
        <ReviewCompare
          originalFeedback={feedback}
          revisedFeedback={revisedFeedback}
          reviewSessions={reviewSessions}
          reviewCount={reviewCount}
        />
      )}
    </div>
  );
}
